import { Dropdown } from '@getsynapse/design-system';
import { isNaN } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import intl from 'react-intl-universal';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Skelton from '../../../../Atoms/Skelton/Skelton';
import FilterButton from '../../../../Organisms/TableHeaderActionButtons/FilterButton';
import TableSkeletonLoader from '../../../../Organisms/TableSkeletonLoader/TableSkeletonLoader';
import {
  getBudgetViewType,
  getLineItemStatus,
  getSelectedBudget,
  setBudgetReportFilters,
  setBudgetViewType,
} from '../../../../state/BudgetList/budgetListSlice';
import {
  createFilterSetting,
  selectFiltersSettingsByType,
  updateFilterSetting,
} from '../../../../state/Settings/Filters/FiltersSlice';
import {
  MONTHS_CAP,
  SETTINGS_FILTERS,
  SLICE_STATUS,
} from '../../../../utils/constants';
import { ActualTimes, BudgetFilters } from '../../../../utils/constants/budget';
import {
  BudgetModel,
  Option,
  TimeFrame,
  TimeOptionsEnum,
} from '../../../../utils/customTypes';
import { ParentOfLineItems } from '../../../../utils/types/budget';
import ReportCard from '../../components/ReportCard';
import useBudgetLineItemCalculations from '../../hooks/useBudgetLineItemCalculations';
import useBudgetViewOptions from '../../hooks/useBudgetViewOptions';
import { useLineItemTotalCalculations } from '../../hooks/useLineItemTotalCalculation';
import BudgetFiltersSidePanel from '../Plan/components/BudgetSidePanelFilters/BudgetFiltersSidePanel';
import BudgetReportChart, { ChartData } from './Components/BudgetReportChart';
import BudgetReportTable from './Components/BudgetReportTable';

const BudgetReport = () => {
  const selectedBudget = useSelector(getSelectedBudget);
  const listStatus = useSelector(getLineItemStatus);
  const [timeOptions, setTimeOptions] = useState<Option[]>([
    {
      label: TimeOptionsEnum.FISCAL_YEAR + ' ' + new Date().getUTCFullYear(),
      value: ActualTimes.FISCAL_CURRENT,
    },
  ]);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(window.location.search);

  const { parentsHavingLineItems } = useBudgetLineItemCalculations();
  const [lineItemChartData, setLineItemChartData] = useState<ChartData[]>([]);
  const selectedBudgetView = useSelector(getBudgetViewType);
  const [activeTimeframe, setActiveTimeframe] = useState<Option>(
    timeOptions[0]
  );

  const viewOptions = useBudgetViewOptions();

  useEffect(() => {
    // Helper function to flatten nested teams recursively
    const flattenTeams = (teams: ParentOfLineItems[]): ParentOfLineItems[] => {
      const flattened: ParentOfLineItems[] = [];

      const processTeam = (team: ParentOfLineItems) => {
        // Add the team if it has line items
        if (team.lineItems && team.lineItems.length > 0) {
          flattened.push(team);
        }

        // Recursively process all children
        if (team.children && team.children.length > 0) {
          team.children.forEach((child) => {
            processTeam(child);
          });
        }
      };

      teams.forEach((team) => {
        processTeam(team);
      });

      return flattened;
    };

    // Flatten the teams to include nested teams
    const flattenedTeams = flattenTeams(parentsHavingLineItems);

    let data: ChartData[] = [];

    if (
      !isNaN(parseFloat(activeTimeframe?.value)) &&
      isFinite(activeTimeframe?.value)
    ) {
      const index = parseFloat(activeTimeframe?.value);

      if (selectedBudget?.timeFrame === TimeFrame.MONTHLY) {
        const fiscalMonthStart = new Date(
          selectedBudget.fiscalPeriodStart
        ).getTime();

        data = flattenedTeams.map((lineItem) => {
          const budgetValue = lineItem.rawAmounts
            ? lineItem.rawAmounts[(fiscalMonthStart + index) % 12]
            : 0;
          const actualValue = lineItem.rawActuals
            ? lineItem.rawActuals[(fiscalMonthStart + index) % 12]
            : 0;

          return {
            name: lineItem.title,
            budget: budgetValue,
            actual: actualValue,
          };
        });
      } else {
        data = flattenedTeams.map((lineItem) => {
          const budgetValue = lineItem.rawAmounts
            ? lineItem.rawAmounts[index % 12]
            : 0;
          const actualValue = lineItem.rawActuals
            ? lineItem.rawActuals[index % 12]
            : 0;

          return {
            name: lineItem.title,
            budget: budgetValue,
            actual: actualValue,
          };
        });
      }
    } else if (activeTimeframe?.value === ActualTimes.FISCAL_CURRENT) {
      data = flattenedTeams.map((lineItem) => {
        const budgetValue = lineItem.rawAmounts
          ? lineItem.rawAmounts.reduce(
              (sum: number, value: number) => sum + value,
              0
            )
          : 0;
        const actualValue = lineItem.rawActuals
          ? lineItem.rawActuals.reduce(
              (sum: number, value: number) => sum + value,
              0
            )
          : 0;

        return {
          name: lineItem.title,
          budget: budgetValue,
          actual: actualValue,
        };
      });
    }

    setLineItemChartData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentsHavingLineItems, activeTimeframe]);

  const generateTimeOptions = (
    selectedBudget: BudgetModel | undefined
  ): Option[] => {
    if (!selectedBudget) {
      return timeOptions;
    }

    const budgetFiscalPeriodStart = new Date(selectedBudget.fiscalPeriodStart);

    const budgetYear = budgetFiscalPeriodStart.getUTCFullYear();
    const budgetMonthStart = budgetFiscalPeriodStart.getUTCMonth();

    let timeOptionsTemp: Option[] = [
      {
        label: TimeOptionsEnum.FISCAL_YEAR + ' ' + budgetYear,
        value: ActualTimes.FISCAL_CURRENT,
      },
    ];

    if (selectedBudget.timeFrame === TimeFrame.MONTHLY) {
      timeOptionsTemp = [
        ...timeOptionsTemp,
        ...Array.from({ length: 12 }, (_, i) => {
          const monthIndex = (budgetMonthStart + i) % 12;
          const year = budgetMonthStart + i < 12 ? budgetYear : budgetYear + 1;
          return {
            label: `${MONTHS_CAP[monthIndex]} ${year}`,
            value: `${monthIndex}`,
          };
        }),
      ];
    }

    if (selectedBudget.timeFrame === TimeFrame.QUARTERLY) {
      timeOptionsTemp = [
        ...timeOptionsTemp,
        {
          label: TimeOptionsEnum.Q1 + ' ' + budgetYear,
          value: '0',
        },
        {
          label: TimeOptionsEnum.Q2 + ' ' + budgetYear,
          value: '1',
        },
        {
          label: TimeOptionsEnum.Q3 + ' ' + budgetYear,
          value: '2',
        },
        {
          label: TimeOptionsEnum.Q4 + ' ' + budgetYear,
          value: '3',
        },
      ];
    }

    return timeOptionsTemp;
  };

  useEffect(() => {
    if (selectedBudget) {
      const options = generateTimeOptions(selectedBudget);
      setTimeOptions(options);
      setActiveTimeframe(options[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBudget]);

  const { sumOfActuals, sumOfAmounts } = useLineItemTotalCalculations(
    activeTimeframe ?? timeOptions[0]
  );

  const onTimeframeChange = (option: Option) => {
    setActiveTimeframe(option);
  };

  const onBudgetViewChange = (option: Option) => {
    dispatch(setBudgetViewType(option));
    const tab = queryParams.get('tab');
    history.push(`${location.pathname}?tab=${tab}&active=${option.value}`);
  };

  const filtersSettings = useSelector(
    selectFiltersSettingsByType(SETTINGS_FILTERS.BUDGET_PLAN_TABLE)
  );

  const [shouldDisplayFilterComponent, setShouldDisplayFilterComponent] =
    useState<boolean>(false);

  const handleSaveFiltersSettings = (updatedFilters: BudgetFilters) => {
    if (filtersSettings && filtersSettings.id) {
      dispatch(
        updateFilterSetting({
          id: filtersSettings.id,
          updateFields: { filter_settings: updatedFilters },
        })
      );
    } else {
      dispatch(
        createFilterSetting({
          filter_type: SETTINGS_FILTERS.BUDGET_PLAN_TABLE,
          filter_settings: updatedFilters,
        })
      );
    }
  };

  const handleUpdateFilters = (updatedFilters: BudgetFilters) => {
    dispatch(setBudgetReportFilters(updatedFilters));
    handleSaveFiltersSettings(updatedFilters);
  };

  const toggleFilterComponent = () =>
    setShouldDisplayFilterComponent((prevState) => !prevState);

  const savedFilters = useMemo<BudgetFilters>(
    () => (filtersSettings ? filtersSettings.settings : {}),
    [filtersSettings]
  );

  useEffect(() => {
    if (savedFilters) {
      dispatch(setBudgetReportFilters(savedFilters));
    }
  }, [savedFilters, dispatch]);

  return (
    <div className='mt-2'>
      <div className='flex justify-between items-center mb-4'>
        <div>
          <Dropdown
            triggerProps={{
              'data-testid': 'budget-viewer-dropdown',
            }}
            popperDiffWidth={true}
            options={viewOptions}
            values={[
              viewOptions.find(
                (option) => option.value === selectedBudgetView?.value
              ),
            ]}
            onChange={onBudgetViewChange}
            placeholder='Team Viewer'
          />
        </div>

        {/* Filters and time options */}
        <div className='flex space-x-2'>
          <FilterButton
            onToggleFilter={() => {
              setShouldDisplayFilterComponent((prevState) => !prevState);
            }}
            aria-pressed={shouldDisplayFilterComponent}
            className='border border-primary'
          />
          <Dropdown
            triggerProps={{
              'data-testid': 'timeframe-dropdown',
            }}
            options={timeOptions}
            onChange={onTimeframeChange}
            values={activeTimeframe ? [activeTimeframe] : []}
            className='w-46'
          />
        </div>
      </div>

      <div className='grid grid-cols-5 gap-x-4 mb-4'>
        {/* Chart */}
        {listStatus === SLICE_STATUS.LOADING && (
          <div className='col-span-4'>
            <Skelton className='w-full h-96' />
          </div>
        )}
        {listStatus === SLICE_STATUS.SUCCESS && (
          <div className='flex flex-col col-span-4 border border-light-pastel-blue shadow-header rounded px-4 py-2'>
            <div className='font-semibold text-xl'>
              {new Date(selectedBudget?.fiscalPeriodStart!).getUTCFullYear()}{' '}
              {intl.get('BUDGET_PAGE.REPORT.FILTERS.BUDGETS_ACTUALS')} (
              {activeTimeframe?.label})
            </div>
            <BudgetReportChart lineItemChartData={lineItemChartData} />
          </div>
        )}

        {/* Report cards */}
        {listStatus === SLICE_STATUS.LOADING && (
          <div className='col-span-1 flex flex-col justify-between'>
            {Array.from({ length: 3 }).map((_, index) => (
              <Skelton key={index} className='w-full h-24' />
            ))}
          </div>
        )}
        {listStatus === SLICE_STATUS.SUCCESS && (
          <div className='col-span-1 flex flex-col justify-between gap-y-4'>
            <ReportCard
              dataTestId='report-total-allocated'
              title={
                intl.get('BUDGET_PAGE.REPORT.TIMEFRAME.TOTAL_ALLOCATED') +
                ' (' +
                activeTimeframe?.label +
                ')'
              }
              value={sumOfAmounts}
            ></ReportCard>
            <ReportCard
              dataTestId='report-total-actuals'
              title={
                intl.get('BUDGET_PAGE.REPORT.TIMEFRAME.ACTUALS') +
                ' (' +
                activeTimeframe?.label +
                ')'
              }
              value={sumOfActuals}
            ></ReportCard>
            <ReportCard
              dataTestId='report-variance'
              title={
                intl.get('BUDGET_PAGE.REPORT.TIMEFRAME.VARIANCE') +
                ' (' +
                activeTimeframe?.label +
                ')'
              }
              value={sumOfAmounts - sumOfActuals}
            ></ReportCard>
          </div>
        )}
      </div>

      {/* Table */}
      {listStatus === SLICE_STATUS.LOADING && <TableSkeletonLoader />}
      {listStatus === SLICE_STATUS.SUCCESS && (
        <BudgetReportTable
          selectedTime={activeTimeframe ?? timeOptions[0]}
          sumOfAmounts={sumOfAmounts}
          sumOfActuals={sumOfActuals}
          toggleFilterComponent={toggleFilterComponent}
          savedFilters={savedFilters}
          handleSaveFiltersSettings={handleSaveFiltersSettings}
        />
      )}

      <BudgetFiltersSidePanel
        open={shouldDisplayFilterComponent || false}
        onClose={toggleFilterComponent}
        filters={savedFilters}
        onUpdateFilters={handleUpdateFilters}
      />
    </div>
  );
};

export default BudgetReport;
